// Polyfills for Node.js APIs used by various libraries
import { Buffer } from 'buffer';

// Make Buffer available globally
window.Buffer = window.Buffer || Buffer;

// Make TextEncoder and TextDecoder available globally if needed
if (typeof window.TextEncoder === 'undefined') {
  const { TextEncoder, TextDecoder } = require('util');
  window.TextEncoder = TextEncoder;
  window.TextDecoder = TextDecoder;
}

// Import the WebAuthn shim first
import './utils/webauthn-shim';

// Main application setup

// WebAuthn polyfill
if (typeof window !== 'undefined' && !window.CredentialsContainer) {
  Object.defineProperty(window, 'CredentialsContainer', {
    value: class CredentialsContainer {
      constructor() {}
      get() { return Promise.resolve(null); }
      create() { return Promise.resolve(null); }
      store() { return Promise.resolve(undefined); }
      preventSilentAccess() { return Promise.resolve(undefined); }
    }
  });
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { validateEnvironment } from './utils/env';
import { debugLog } from './utils/debug';
import { initializeClientIdHandling } from './services/api/clientIdSetup';
import { setDockerEnvironment } from './utils/api';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin, withAITracking } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import './index.css';

// Initialize Application Insights
const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();
let appInsights: ApplicationInsights | null = null;

const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

if (connectionString) {
  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: { history: browserHistory }
        },
        // COMPLETELY DISABLE automatic tracking to eliminate noise
        enableAutoRouteTracking: false,
        disableAjaxTracking: true, 
        autoTrackPageVisitTime: false,
        enableCorsCorrelation: false,
        enableRequestHeaderTracking: false,
        enableResponseHeaderTracking: false,
        enableUnhandledPromiseRejectionTracking: true, // Keep errors

        // Exception tracking is enabled by default - no need for explicit config
        // Exclude noisy domains and health checks
        excludeRequestFromAutoTrackingPatterns: [
          /\/health$/i,        // Health check endpoints  
          /\/healthcheck$/i,   // Health check variants
          /\/ping$/i,          // Ping endpoints
          /\/status$/i,        // Status endpoints
          /\/metrics$/i,       // Metrics endpoints
          /\/favicon\.ico$/i,  // Favicon requests
          /\.(css|js|png|jpg|gif|svg|woff|woff2)$/i, // Static assets
        ],
        correlationHeaderExcludedDomains: ['dacgroup.us.qlikcloud.com', '*.qlikcloud.com'],
      }
    });
    appInsights.loadAppInsights();
    // REMOVED: trackPageView() - no longer tracking page views
    
    // Make appInsights globally available for error logging
    (window as any).__appInsights = appInsights;
    
    debugLog('Main', 'Application Insights initialized.');
  } catch (error) {
    debugLog('Main', 'Failed to initialize Application Insights:', error);
    // Optionally render an error message or log to console
  }
} else {
  debugLog('Main', 'Application Insights connection string not found. Telemetry disabled.');
}

// Wrap App component with AI tracking if initialized
const AppWithAI = appInsights ? withAITracking(reactPlugin, App) : App;


// Environment validation error component
const EnvironmentError: React.FC<{ error: string }> = ({ error }) => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
      <h1 className="text-2xl font-bold text-red-600 mb-4">Environment Configuration Error</h1>
      <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
        <p className="text-red-700 whitespace-pre-wrap">{error}</p>
      </div>
      <div className="text-sm text-gray-600">
        <p className="mb-2">Please check your environment configuration:</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Ensure all required environment variables are set in your .env file</li>
          <li>Verify the values are in the correct format</li>
          <li>Check that your .env file is in the correct location</li>
          <li>Restart the development server after making changes</li>
        </ol>
      </div>
    </div>
  </div>
);

// Initialize the application
// Initialize Docker environment detection
function initializeDockerEnvironment() {
  // Check if we're in a container environment
  const inContainer = import.meta.env.VITE_CONTAINER_ENVIRONMENT === 'true';
  const containerFlag = (window as any).CONTAINER_ENVIRONMENT === 'true';
  
  debugLog('Main', `Container environment check: env=${inContainer}, window=${containerFlag}`);
  
  // Check Docker-specific environment markers
  if (inContainer || containerFlag) {
    debugLog('Main', 'Docker container environment detected, setting container_environment flag');
    try {
      localStorage.setItem('container_environment', 'true');
    } catch (e) {
      console.warn('Failed to store container_environment in localStorage:', e);
    }
  } else if (
    // Also check for container hostnames (not localhost)
    window.location.hostname !== 'localhost' && 
    window.location.hostname !== '127.0.0.1'
  ) {
    debugLog('Main', `Non-localhost hostname detected: ${window.location.hostname}, likely in container`);
    try {
      localStorage.setItem('container_environment', 'true');
    } catch (e) {
      console.warn('Failed to store container_environment in localStorage:', e);
    }
  }
  
  // Log the current environment detection status
  try {
    const containerEnvironment = localStorage.getItem('container_environment') === 'true';
    debugLog('Main', `Container environment detection result: ${containerEnvironment}`);
  } catch (e) {
    debugLog('Main', 'Failed to check container_environment in localStorage:', e);
  }
}

function initializeApp() {
  try {
    // Initialize Docker environment detection
    initializeDockerEnvironment();
    
    // Validate environment variables
    validateEnvironment();
    
    // Initialize client ID handling for API requests
    debugLog('Main', 'Initializing client ID handling for API requests');
    initializeClientIdHandling();
    
    // If validation passes, render the app (wrapped with AI tracking if enabled)
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <AppWithAI />
      </React.StrictMode>
    );
  } catch (error) {
    debugLog('Main', 'Environment validation failed:', error);
    
    // Render error UI if environment validation fails
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <EnvironmentError error={error instanceof Error ? error.message : 'Unknown environment error'} />
      </React.StrictMode>
    );
  }
}

// Start the application
initializeApp();
